import { useState, useEffect, useCallback, useRef } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Database, Trash, CloudArrowDown, Plug, CheckCircle, XCircle,
  CircleNotch, WarningCircle, MagnifyingGlass, Package, Receipt, Barcode,
  UploadSimple, FileZip, ArrowsClockwise, Broom,
} from '@phosphor-icons/react';
import { zohoHistoricalAPI, zohoDumpAPI, billingOpsAPI } from '@/lib/api';

const fmt = (n) => (n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const MODULE_LABELS = {
  chart_of_accounts: 'Chart of Accounts',
  bank_accounts: 'Bank Accounts',
  customers: 'Customers',
  vendors_and_bills: 'Vendors & Bills',
  vendors: 'Vendors',
  vendor_bills: 'Vendor Bills',
  items: 'Items / Inventory',
  invoices: 'Invoices',
  sales_orders: 'Sales Orders',
  payments: 'Payments',
  credit_notes: 'Credit Notes',
};

export default function ZohoSyncPage() {
  const [status, setStatus] = useState(null);
  const [conn, setConn] = useState(null);
  const [dateStart, setDateStart] = useState('2023-04-01');
  const [dateEnd, setDateEnd] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState('');
  const pollRef = useRef(null);

  const refreshStatus = useCallback(async () => {
    try {
      const res = await zohoHistoricalAPI.status();
      setStatus(res.data);
    } catch (e) {
      console.warn('status failed', e);
    }
  }, []);

  useEffect(() => {
    refreshStatus();
    // Test connection on mount
    zohoHistoricalAPI.testConnection()
      .then(res => setConn({ ok: true, org: res.data.organization }))
      .catch(err => setConn({ ok: false, error: err?.response?.data?.detail || err.message }));
  }, [refreshStatus]);

  // Auto-poll when sync is running
  useEffect(() => {
    const running = status?.job?.status === 'running';
    if (running && !pollRef.current) {
      pollRef.current = setInterval(refreshStatus, 4000);
    }
    if (!running && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [status?.job?.status, refreshStatus]);

  const handleWipe = async () => {
    if (wipeConfirm !== 'WIPE-NON-HR-DATA') {
      toast.error('Type exactly: WIPE-NON-HR-DATA');
      return;
    }
    if (!window.confirm('This will delete ALL invoices, sales orders, credit notes, vendor data, inventory, and Zoho legacy data. HR data will be preserved. Continue?')) return;
    setBusy(true);
    try {
      const res = await zohoHistoricalAPI.wipe();
      toast.success(`Wipe complete: ${Object.keys(res.data.results).length} collections cleared.`);
      setWipeConfirm('');
      refreshStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Wipe failed');
    } finally {
      setBusy(false);
    }
  };

  const handleStart = async () => {
    if (status?.job?.status === 'running') {
      toast.error('A sync is already in progress.');
      return;
    }
    if (!window.confirm(`Start full Zoho import from ${dateStart} to ${dateEnd}? This may take 20-60 minutes. You can leave this page open and check back.`)) return;
    setBusy(true);
    try {
      await zohoHistoricalAPI.start(dateStart, dateEnd);
      toast.success('Historical sync started in background.');
      refreshStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start sync');
    } finally {
      setBusy(false);
    }
  };

  const job = status?.job || {};
  const dbCounts = status?.db_counts || {};
  const rate = status?.rate_status || {};
  const isRunning = job.status === 'running';

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto" data-testid="zoho-historical-page">
      <PageHeader
        title="Zoho Historical Import"
        subtitle="One-time bulk import from Zoho Books. After import, all data lives in MongoDB and Zoho can be retired."
      />

      {/* Connection */}
      <Card data-testid="conn-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base"><Plug size={18}/> Zoho Connection</CardTitle>
          <Button size="sm" variant="outline" onClick={() => {
            setConn(null);
            zohoHistoricalAPI.testConnection()
              .then(res => setConn({ ok: true, org: res.data.organization }))
              .catch(err => setConn({ ok: false, error: err?.response?.data?.detail || err.message }));
          }} data-testid="test-conn-btn">Re-test</Button>
        </CardHeader>
        <CardContent>
          {!conn && <div className="flex items-center gap-2 text-[#64748B]"><CircleNotch className="animate-spin" size={16}/> Testing…</div>}
          {conn?.ok && (
            <div className="flex items-center gap-2 text-emerald-700" data-testid="conn-ok">
              <CheckCircle size={18}/> Connected to <strong>{conn.org?.name}</strong>
              <span className="text-xs text-[#64748B] ml-2">(Org ID: {conn.org?.organization_id})</span>
            </div>
          )}
          {conn && !conn.ok && (
            <div className="flex items-center gap-2 text-rose-700" data-testid="conn-fail">
              <XCircle size={18}/> Connection failed: {conn.error}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 1: Wipe */}
      <Card data-testid="wipe-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base text-rose-700">
            <Trash size={18}/> Step 1 — Wipe Non-HR Data
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm text-[#64748B]">
            Deletes all <strong>invoices, sales orders, credit notes, customers, vendors, vendor bills,
            inventory, items master, bank statements, GST entries, chart of accounts, audit logs,
            and the entire <code>legacy_zoho_*</code> archive</strong>. Your HR data (employees, clinics,
            payroll, attendance) remains untouched.
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
            <div className="flex-1">
              <Label htmlFor="wipe-token" className="text-xs">Type <code>WIPE-NON-HR-DATA</code> to confirm</Label>
              <Input
                id="wipe-token"
                value={wipeConfirm}
                onChange={(e) => setWipeConfirm(e.target.value)}
                placeholder="WIPE-NON-HR-DATA"
                data-testid="wipe-confirm-input"
              />
            </div>
            <Button
              variant="destructive"
              onClick={handleWipe}
              disabled={busy || wipeConfirm !== 'WIPE-NON-HR-DATA'}
              data-testid="wipe-btn"
            >
              <Trash size={16}/> Wipe Now
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Step 2: Start sync */}
      <Card data-testid="start-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base text-emerald-700">
            <CloudArrowDown size={18}/> Step 2 — Pull Everything from Zoho
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ds" className="text-xs">From date</Label>
              <Input
                id="ds"
                type="date"
                value={dateStart}
                onChange={(e) => setDateStart(e.target.value)}
                data-testid="date-start-input"
              />
            </div>
            <div>
              <Label htmlFor="de" className="text-xs">To date</Label>
              <Input
                id="de"
                type="date"
                value={dateEnd}
                onChange={(e) => setDateEnd(e.target.value)}
                data-testid="date-end-input"
              />
            </div>
          </div>
          <div className="text-xs text-[#64748B] flex items-start gap-2">
            <WarningCircle size={14} className="mt-0.5 text-amber-600"/>
            Customers, vendors, items, chart of accounts &amp; bank accounts are always fetched in full (no date filter). Invoices, sales orders, payments, credit notes &amp; bills use the date range.
          </div>
          <Button
            onClick={handleStart}
            disabled={busy || isRunning || !conn?.ok}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="start-sync-btn"
          >
            {isRunning ? <><CircleNotch className="animate-spin" size={16}/> Sync running…</> : <><CloudArrowDown size={16}/> Start Historical Import</>}
          </Button>
        </CardContent>
      </Card>

      {/* Live progress */}
      <Card data-testid="progress-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base"><Database size={18}/> Live Progress</CardTitle>
          <Badge variant={
            job.status === 'running' ? 'default' :
            job.status === 'completed' ? 'success' :
            job.status === 'failed' ? 'destructive' :
            'secondary'
          } data-testid="job-status-badge">
            {job.status || 'idle'}
          </Badge>
        </CardHeader>
        <CardContent>
          {!job.status || job.status === 'idle' ? (
            <div className="text-sm text-[#64748B] text-center py-6">
              No sync has been run yet. Wipe data, then click <strong>Start Historical Import</strong>.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div><span className="text-[#64748B]">Started:</span> {job.started_at?.slice(0, 19).replace('T', ' ')}</div>
                <div><span className="text-[#64748B]">Range:</span> {job.date_start} → {job.date_end}</div>
                <div><span className="text-[#64748B]">Current:</span> <strong>{MODULE_LABELS[job.current_module] || job.current_module || '—'}</strong></div>
                {job.completed_at && <div><span className="text-[#64748B]">Completed:</span> {job.completed_at.slice(0, 19).replace('T', ' ')}</div>}
                <div><span className="text-[#64748B]">Zoho API used today:</span> {rate.calls_made_this_session || 0} / {rate.limit > 0 ? rate.limit : '?'}</div>
                <div><span className="text-[#64748B]">Remaining:</span> {rate.remaining >= 0 ? rate.remaining : '?'}</div>
              </div>

              <table className="w-full text-sm" data-testid="progress-table">
                <thead>
                  <tr className="border-b text-left text-xs text-[#64748B]">
                    <th className="py-2">Module</th>
                    <th className="py-2 text-right">Fetched</th>
                    <th className="py-2 text-right">In DB</th>
                    <th className="py-2 text-right">Last update</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(job.progress || {}).map(([m, p]) => {
                    const dbKey = ({
                      chart_of_accounts: 'chart_of_accounts',
                      bank_accounts: 'bank_accounts',
                      customers: 'billing_customers',
                      vendors_and_bills: null,
                      vendors: 'vendors',
                      vendor_bills: 'vendor_bills',
                      items: 'items_master',
                      invoices: 'billing_invoices',
                      sales_orders: 'billing_sales_orders',
                      payments: 'billing_payments',
                      credit_notes: 'billing_credit_notes',
                    })[m];
                    const inDb = dbKey ? dbCounts[dbKey] : '';
                    return (
                      <tr key={m} className="border-b" data-testid={`row-${m}`}>
                        <td className="py-2">{MODULE_LABELS[m] || m}</td>
                        <td className="py-2 text-right tabular-nums">{fmt(p.fetched)}</td>
                        <td className="py-2 text-right tabular-nums">{inDb !== '' && inDb !== undefined ? fmt(inDb) : '—'}</td>
                        <td className="py-2 text-right text-xs text-[#64748B]">{p.last_at ? p.last_at.slice(11, 19) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {(job.errors && job.errors.length > 0) && (
                <div className="bg-rose-50 border border-rose-200 rounded p-3 text-sm" data-testid="errors-box">
                  <div className="font-semibold text-rose-700 mb-1">Errors:</div>
                  <ul className="list-disc ml-5 text-rose-700 text-xs space-y-1">
                    {job.errors.map((e, i) => <li key={i}><strong>{e.module}:</strong> {e.error}</li>)}
                  </ul>
                </div>
              )}

              {job.status === 'completed' && (
                <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm text-emerald-700 flex items-center gap-2">
                  <CheckCircle size={18}/> Sync complete! Your dashboards, invoices and reports are now populated with Zoho historical data.
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <IncrementalSyncCard />
      <DumpImportCard />
      <IncrementalImportCard />
      <PullActiveSerialsCard />
      <PerRecordReconCard />
      <ReconciliationCard />
      <DeepEnrichmentCard />
      <EnrichmentCard />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Zoho Dump Import — merge an offline Zoho export (.xls) that carries the
// REAL serial numbers, full line items, locations & lead sources the API
// never returns. Upload a .zip (or use a server-side dump) → full merge.
// ──────────────────────────────────────────────────────────────────────
const DUMP_MODULE_LABELS = {
  customers: 'Customers', items: 'Items / Inventory', invoices: 'Invoices',
  sales_orders: 'Sales Orders', bills: 'Vendor Bills', payments: 'Payments',
  credit_notes: 'Credit Notes', vendor_payments: 'Vendor Payments',
  serials: 'Inventory Serials', transfer_orders: 'Transfer Orders',
};

function DumpImportCard() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const res = await zohoDumpAPI.status();
      setS(res.data);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const running = s?.job?.status === 'running';
    if (running && !pollRef.current) pollRef.current = setInterval(refresh, 3000);
    if (!running && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [s?.job?.status, refresh]);

  const start = async () => {
    setBusy(true);
    try {
      await zohoDumpAPI.start(null);
      toast.success('Dump import started');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start import');
    } finally {
      setBusy(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.zip')) {
      toast.error('Please select a .zip Zoho export');
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    setUploading(true);
    setUploadPct(0);
    try {
      // Chunked upload (5 MB) — bypasses the proxy body-size limit for large dumps.
      const CHUNK = 5 * 1024 * 1024;
      const total = Math.ceil(file.size / CHUNK);
      const uploadId = `dump-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CHUNK, Math.min((i + 1) * CHUNK, file.size));
        const fd = new FormData();
        fd.append('upload_id', uploadId);
        fd.append('chunk_index', String(i));
        fd.append('total_chunks', String(total));
        fd.append('file', blob, `${file.name}.part${i}`);
        await zohoDumpAPI.uploadChunk(fd);
        setUploadPct(Math.round(((i + 1) / total) * 100));
      }
      const ff = new FormData();
      ff.append('upload_id', uploadId);
      const res = await zohoDumpAPI.uploadFinalize(ff);
      toast.success(`Uploaded · ${res.data.file_count} files extracted`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadPct(0);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const job = s?.job || {};
  const isRunning = job.status === 'running';
  const progress = job.progress || {};
  const dumpPresent = !!s?.dump_dir;

  return (
    <Card data-testid="dump-import-card" className="border-indigo-300">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-base text-indigo-700">
          <FileZip size={18}/> Import from Zoho Dump (.xls export) — real serials &amp; full detail
        </CardTitle>
        <Badge variant={isRunning ? 'default' : 'secondary'} data-testid="dump-status-badge">{job.status || 'idle'}</Badge>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-[#64748B]">
          Merges an offline Zoho Books export — the only source that carries the{' '}
          <strong>real serial numbers, full line items, warehouse locations, salesperson &amp; lead source</strong>{' '}
          against every invoice, bill &amp; item. Matches by Zoho ID and fully refreshes each record.
          Runs entirely offline — <span className="text-indigo-700">no Zoho API calls, no rate limits</span>.
          Credit Notes are never touched.
        </p>

        <div className="flex items-center gap-2 text-xs">
          {dumpPresent ? (
            <Badge variant="secondary" className="bg-emerald-50 text-emerald-700" data-testid="dump-present-badge">
              <CheckCircle size={12} className="mr-1"/> Dump ready: {s.dump_dir}
            </Badge>
          ) : (
            <Badge variant="secondary" className="bg-amber-50 text-amber-700" data-testid="dump-missing-badge">
              <WarningCircle size={12} className="mr-1"/> No dump found — upload a .zip
            </Badge>
          )}
          {Array.isArray(s?.files) && s.files.length > 0 && (
            <span className="text-[#94A3B8]">{s.files.length} files</span>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <input ref={fileRef} type="file" accept=".zip" onChange={onUpload} className="hidden" data-testid="dump-file-input" />
          <Button
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={uploading || isRunning}
            data-testid="dump-upload-btn"
          >
            {uploading ? <><CircleNotch className="animate-spin" size={16}/> Uploading… {uploadPct}%</> : <><UploadSimple size={16}/> Upload Dump (.zip)</>}
          </Button>
          <Button
            onClick={start}
            disabled={busy || isRunning || !dumpPresent}
            className="bg-indigo-700 hover:bg-indigo-800 text-white"
            data-testid="dump-start-btn"
          >
            {isRunning ? <><CircleNotch className="animate-spin" size={16}/> Importing…</> : <><Database size={16}/> Start Dump Import</>}
          </Button>
        </div>

        {(isRunning || job.status === 'completed' || job.status === 'error') && (
          <table className="w-full text-sm border-t pt-2" data-testid="dump-progress-table">
            <thead>
              <tr className="border-b text-left text-xs text-[#64748B]">
                <th className="py-2">Module</th>
                <th className="py-2 text-right">State</th>
                <th className="py-2 text-right">Parsed</th>
                <th className="py-2 text-right">Written</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(progress).map(([m, p]) => (
                <tr key={m} className="border-b" data-testid={`dump-row-${m}`}>
                  <td className="py-2">{DUMP_MODULE_LABELS[m] || m}</td>
                  <td className="py-2 text-right text-xs">
                    {p.state === 'done'
                      ? <span className="text-emerald-700">done</span>
                      : <span className="text-indigo-700 inline-flex items-center gap-1"><CircleNotch className="animate-spin" size={12}/> {p.state}</span>}
                  </td>
                  <td className="py-2 text-right tabular-nums">{fmt(p.parsed || 0)}</td>
                  <td className="py-2 text-right tabular-nums text-emerald-700">{fmt(p.written || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {job.status === 'completed' && (
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm text-emerald-700 flex items-center gap-2" data-testid="dump-complete-banner">
            <CheckCircle size={18}/> Dump import complete · {job.finished_at?.slice(0, 19).replace('T', ' ')}
          </div>
        )}

        {job.status === 'error' && (
          <div className="bg-rose-50 border border-rose-200 rounded p-3 text-sm text-rose-700" data-testid="dump-error-box">
            <strong>Error:</strong> {job.error}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Incremental Import — add-only upsert of a weekly Zoho batch (invoices,
// customers, sales orders, bills, payments) + FULL-REPLACE of transfer
// orders. Handles Zoho's " (N)" filenames. Also cleans duplicate invoice #s.
// ──────────────────────────────────────────────────────────────────────
function IncrementalImportCard() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [dedupeBusy, setDedupeBusy] = useState(false);
  const [dedupeRes, setDedupeRes] = useState(null);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const res = await zohoDumpAPI.incrementalStatus();
      setS(res.data);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const running = s?.job?.status === 'running';
    if (running && !pollRef.current) pollRef.current = setInterval(refresh, 2500);
    if (!running && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [s?.job?.status, refresh]);

  const start = async () => {
    setBusy(true);
    try {
      await zohoDumpAPI.incrementalStart();
      toast.success('Incremental import started');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start import');
    } finally {
      setBusy(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.zip')) {
      toast.error('Please select a .zip Zoho export');
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    setUploading(true);
    setUploadPct(0);
    try {
      const CHUNK = 5 * 1024 * 1024;
      const total = Math.ceil(file.size / CHUNK);
      const uploadId = `incr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CHUNK, Math.min((i + 1) * CHUNK, file.size));
        const fd = new FormData();
        fd.append('upload_id', uploadId);
        fd.append('chunk_index', String(i));
        fd.append('total_chunks', String(total));
        fd.append('file', blob, `${file.name}.part${i}`);
        await zohoDumpAPI.uploadChunk(fd);
        setUploadPct(Math.round(((i + 1) / total) * 100));
      }
      const ff = new FormData();
      ff.append('upload_id', uploadId);
      const res = await zohoDumpAPI.incrementalFinalize(ff);
      const n = Object.keys(res.data?.files_detected || {}).length;
      toast.success(`Uploaded · ${n} data file(s) detected`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadPct(0);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const cleanDuplicates = async () => {
    setDedupeBusy(true);
    setDedupeRes(null);
    try {
      const res = await billingOpsAPI.dedupeInvoiceNumbers();
      setDedupeRes(res.data);
      toast.success(`Removed ${res.data.removed} duplicate invoice number(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cleanup failed');
    } finally {
      setDedupeBusy(false);
    }
  };

  const job = s?.job || {};
  const isRunning = job.status === 'running';
  const progress = job.progress || {};
  const deltas = job.deltas || {};
  const detected = s?.files_detected || [];

  return (
    <Card data-testid="incr-import-card" className="border-teal-300">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-base text-teal-700">
          <ArrowsClockwise size={18}/> Upload Zoho Batch — incremental add + transfer-order refresh
        </CardTitle>
        <Badge variant={isRunning ? 'default' : 'secondary'} data-testid="incr-status-badge">{job.status || 'idle'}</Badge>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-[#64748B]">
          Upload a small Zoho export .zip (e.g. a weekly batch). New{' '}
          <strong>invoices, customers, sales orders, vendor bills, payments, credit notes &amp; vendor payments</strong> are added/merged by Zoho ID —
          existing records are never duplicated. <strong>Transfer Orders are fully re-imported</strong> (day-1 authoritative)
          to keep counts exact. Runs offline; the destructive serial rebuild is never triggered.
          Handles Zoho&apos;s <code>Invoice (6).xlsx</code> style filenames automatically.
        </p>

        <div className="flex items-center gap-2 text-xs flex-wrap">
          {detected.length > 0 ? (
            <Badge variant="secondary" className="bg-emerald-50 text-emerald-700" data-testid="incr-detected-badge">
              <CheckCircle size={12} className="mr-1"/> {detected.length} file(s): {detected.join(', ')}
            </Badge>
          ) : (
            <Badge variant="secondary" className="bg-amber-50 text-amber-700" data-testid="incr-missing-badge">
              <WarningCircle size={12} className="mr-1"/> No batch uploaded yet
            </Badge>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <input ref={fileRef} type="file" accept=".zip" onChange={onUpload} className="hidden" data-testid="incr-file-input" />
          <Button
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={uploading || isRunning}
            data-testid="incr-upload-btn"
          >
            {uploading ? <><CircleNotch className="animate-spin" size={16}/> Uploading… {uploadPct}%</> : <><UploadSimple size={16}/> Upload Batch (.zip)</>}
          </Button>
          <Button
            onClick={start}
            disabled={busy || isRunning || detected.length === 0}
            className="bg-teal-700 hover:bg-teal-800 text-white"
            data-testid="incr-start-btn"
          >
            {isRunning ? <><CircleNotch className="animate-spin" size={16}/> Importing…</> : <><Database size={16}/> Start Incremental Import</>}
          </Button>
        </div>

        {(isRunning || job.status === 'completed' || job.status === 'error') && (
          <table className="w-full text-sm border-t pt-2" data-testid="incr-progress-table">
            <thead>
              <tr className="border-b text-left text-xs text-[#64748B]">
                <th className="py-2">Module</th>
                <th className="py-2 text-right">State</th>
                <th className="py-2 text-right">Parsed</th>
                <th className="py-2 text-right">Net added</th>
              </tr>
            </thead>
            <tbody>
              {['customers', 'invoices', 'sales_orders', 'bills', 'payments', 'credit_notes', 'vendor_payments', 'transfer_orders'].map((m) => {
                const p = progress[m] || {};
                const coll = { customers: 'customers', invoices: 'billing_invoices', sales_orders: 'billing_sales_orders', bills: 'vendor_bills', payments: 'billing_payments', credit_notes: 'billing_credit_notes', vendor_payments: 'vendor_payments', transfer_orders: 'inventory_transfers' }[m];
                const d = deltas[coll];
                return (
                  <tr key={m} className="border-b" data-testid={`incr-row-${m}`}>
                    <td className="py-2">{INCR_LABELS[m]}</td>
                    <td className="py-2 text-right text-xs">
                      {p.state === 'done'
                        ? <span className="text-emerald-700">done{p.replaced ? ' · replaced' : ''}</span>
                        : p.state === 'skipped'
                          ? <span className="text-[#94A3B8]">skipped</span>
                          : p.state
                            ? <span className="text-teal-700 inline-flex items-center gap-1"><CircleNotch className="animate-spin" size={12}/> {p.state}</span>
                            : '—'}
                    </td>
                    <td className="py-2 text-right tabular-nums">{fmt(p.parsed || 0)}</td>
                    <td className="py-2 text-right tabular-nums text-emerald-700">{d != null ? (d >= 0 ? `+${fmt(d)}` : fmt(d)) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {job.status === 'completed' && (
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm text-emerald-700 flex items-center gap-2" data-testid="incr-complete-banner">
            <CheckCircle size={18}/> Import complete · {job.finished_at?.slice(0, 19).replace('T', ' ')}
          </div>
        )}
        {job.status === 'error' && (
          <div className="bg-rose-50 border border-rose-200 rounded p-3 text-sm text-rose-700" data-testid="incr-error-box">
            <strong>Error:</strong> {job.error}
          </div>
        )}

        <div className="border-t pt-3 space-y-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-sm text-[#64748B]">
              Clean duplicate invoice numbers — removes ₹0 duplicate copies, keeps every real invoice.
            </span>
            <Button
              variant="outline"
              onClick={cleanDuplicates}
              disabled={dedupeBusy}
              data-testid="incr-dedupe-invoices-btn"
            >
              {dedupeBusy ? <><CircleNotch className="animate-spin" size={16}/> Cleaning…</> : <><Broom size={16}/> Fix Duplicate Invoice #s</>}
            </Button>
          </div>
          {dedupeRes && (
            <div className="bg-teal-50 border border-teal-200 rounded p-3 text-sm text-teal-800" data-testid="incr-dedupe-result">
              Removed <strong>{dedupeRes.removed}</strong> ₹0 duplicate invoice(s).
              {dedupeRes.skipped_count > 0 && (
                <span> {dedupeRes.skipped_count} number(s) left for manual review (both copies real): {dedupeRes.skipped.map((x) => x.invoice_number).join(', ')}.</span>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const INCR_LABELS = {
  customers: 'Customers', invoices: 'Invoices', sales_orders: 'Sales Orders',
  bills: 'Vendor Bills', payments: 'Payments', credit_notes: 'Credit Notes',
  vendor_payments: 'Vendor Payments', transfer_orders: 'Transfer Orders',
};

// ──────────────────────────────────────────────────────────────────────
// Incremental Sync — pulls only new / modified records from Zoho since
// the last sync. Auto-detects the "since" date per module.
// ──────────────────────────────────────────────────────────────────────
function IncrementalSyncCard() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [overrideSince, setOverrideSince] = useState('');
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const res = await zohoHistoricalAPI.incrementalStatus();
      setS(res.data);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const running = s?.job?.status === 'running';
    if (running && !pollRef.current) pollRef.current = setInterval(refresh, 4000);
    if (!running && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [s?.job?.status, refresh]);

  const start = async () => {
    setBusy(true);
    try {
      await zohoHistoricalAPI.incrementalStart(overrideSince || null, null);
      toast.success('Incremental sync started');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start');
    } finally {
      setBusy(false);
    }
  };

  const job = s?.job || {};
  const isRunning = job.status === 'running';
  const nextSince = s?.next_since || {};
  const progress = job.progress || {};
  const rate = s?.rate_status || {};

  // Pretty earliest "since" across modules (what the user would assume)
  const earliestSince = Object.values(nextSince).sort()[0] || '—';

  return (
    <Card data-testid="incremental-sync-card" className="border-emerald-300">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-base text-emerald-700">
          <CloudArrowDown size={18}/> Incremental Sync — Catch up from last sync to today
        </CardTitle>
        <Badge variant={isRunning ? 'default' : 'secondary'} data-testid="incr-status-badge">{job.status || 'idle'}</Badge>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-[#64748B]">
          Pulls only records <strong>created or modified</strong> in Zoho since the last sync, then
          upserts them into Fintexa (no duplicates). Refreshes invoices, sales orders, payments,
          credit notes, customers, items &amp; vendor bills.
          <br/>
          <span className="text-emerald-700">Cost: a few hundred API calls (vs. ~30,000 for full sync).</span>
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
          {Object.entries(nextSince).map(([m, d]) => (
            <div key={m} className="border rounded p-2" data-testid={`incr-since-${m}`}>
              <div className="text-[#64748B] capitalize">{MODULE_LABELS[m] || m}</div>
              <div className="font-semibold tabular-nums">{d}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
          <div className="flex-1">
            <Label htmlFor="override-since" className="text-xs">
              Override "since" date <span className="text-[#94A3B8]">(optional — leave blank to auto-detect per module)</span>
            </Label>
            <Input
              id="override-since"
              type="date"
              value={overrideSince}
              onChange={(e) => setOverrideSince(e.target.value)}
              data-testid="incr-override-since"
            />
          </div>
          <Button
            onClick={start}
            disabled={busy || isRunning}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="incr-start-btn"
          >
            {isRunning ? <><CircleNotch className="animate-spin" size={16}/> Running…</> : <><CloudArrowDown size={16}/> Start Incremental Sync</>}
          </Button>
        </div>

        <div className="text-xs text-[#64748B]">
          Earliest "since" date in next run: <strong className="tabular-nums">{earliestSince}</strong>
          {rate.remaining >= 0 && <> · Zoho calls left today: <strong>{rate.remaining}</strong></>}
        </div>

        {/* Live progress table */}
        {(isRunning || job.status === 'completed' || job.status === 'failed') && (
          <table className="w-full text-sm border-t pt-2" data-testid="incr-progress-table">
            <thead>
              <tr className="border-b text-left text-xs text-[#64748B]">
                <th className="py-2">Module</th>
                <th className="py-2 text-right">Since</th>
                <th className="py-2 text-right">New</th>
                <th className="py-2 text-right">Updated</th>
                <th className="py-2 text-right">Last update</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(progress).map(([m, p]) => (
                <tr key={m} className="border-b" data-testid={`incr-row-${m}`}>
                  <td className="py-2">{MODULE_LABELS[m] || m}</td>
                  <td className="py-2 text-right tabular-nums text-xs text-[#64748B]">{job.since_map?.[m] || '—'}</td>
                  <td className="py-2 text-right tabular-nums text-emerald-700">{fmt(p.new || 0)}</td>
                  <td className="py-2 text-right tabular-nums text-blue-700">{fmt(p.updated || 0)}</td>
                  <td className="py-2 text-right text-xs text-[#64748B]">{p.last_at ? p.last_at.slice(11, 19) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {job.status === 'completed' && (
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm text-emerald-700 flex items-center gap-2" data-testid="incr-complete-banner">
            <CheckCircle size={18}/> Incremental sync complete · {job.completed_at?.slice(0, 19).replace('T', ' ')}
          </div>
        )}

        {job.errors && job.errors.length > 0 && (
          <div className="bg-rose-50 border border-rose-200 rounded p-3 text-sm" data-testid="incr-errors-box">
            <div className="font-semibold text-rose-700 mb-1">Errors:</div>
            <ul className="list-disc ml-5 text-rose-700 text-xs space-y-1">
              {job.errors.map((e, i) => <li key={i}><strong>{e.module}:</strong> {e.error}</li>)}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Pull every active serial from Zoho via the official /items/serialnumbers
// endpoint. This is the SAME data Zoho's "Serial Number Details" export uses.
// ──────────────────────────────────────────────────────────────────────
function PullActiveSerialsCard() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const res = await zohoHistoricalAPI.pullActiveSerialsStatus();
      setS(res.data);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const running = s?.job?.status === 'running';
    if (running && !pollRef.current) pollRef.current = setInterval(refresh, 4000);
    if (!running && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [s?.job?.status, refresh]);

  const start = async () => {
    setBusy(true);
    try {
      await zohoHistoricalAPI.pullActiveSerials();
      toast.success('Pull started in background');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Pull failed to start');
    } finally { setBusy(false); }
  };

  const job = s?.job || {};
  const isRunning = job.status === 'running';
  const totalApi = s?.total_serials_from_api || 0;
  const rate = s?.rate_status || {};

  return (
    <Card data-testid="pull-active-serials-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-base">
          <Barcode size={18}/> Pull Real Serial Numbers from Zoho
        </CardTitle>
        <Badge variant={isRunning ? 'default' : 'secondary'}>{job.status || 'idle'}</Badge>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-[#64748B]">
          Uses Zoho's official <code>GET /items/serialnumbers?item_id={'{id}'}</code> endpoint —
          the same data source as the Zoho UI's <em>Serial Number Details</em> export.
          For every item, pulls every active serial along with its current warehouse (via
          <code> items/{'{id}'}.locations[].serial_number_details</code>) and upserts into
          <code> inventory_serials</code> with real <code>serialnumber_id</code>, status, and
          clinic mapping.
          <br/>
          <span className="text-emerald-700">Cost: ~2 Zoho calls per item (1,058 items → ~2,100 calls).</span>
          <span className="ml-2">Rate-limited 9k/day, resume-safe.</span>
          <br/>
          <strong>No dummies, no FIFO heuristics — only real Zoho serials.</strong>
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div className="border rounded p-2">
            <div className="text-[#64748B]">Items processed</div>
            <div className="font-bold text-lg">{(job.progress?.items_processed || 0).toLocaleString()}</div>
          </div>
          <div className="border rounded p-2">
            <div className="text-[#64748B]">Items with serials</div>
            <div className="font-bold text-lg">{(job.progress?.items_with_serials || 0).toLocaleString()}</div>
          </div>
          <div className="border rounded p-2">
            <div className="text-[#64748B]">Serials in DB</div>
            <div className="font-bold text-lg text-emerald-700">{totalApi.toLocaleString()}</div>
          </div>
          <div className="border rounded p-2">
            <div className="text-[#64748B]">Zoho calls left today</div>
            <div className="font-bold text-lg">{rate.remaining ?? '—'}</div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="text-xs text-[#64748B]">
            {job.started_at && <>Started: {job.started_at.slice(0, 19).replace('T', ' ')}</>}
            {job.completed_at && <span className="ml-3">Completed: {job.completed_at.slice(0, 19).replace('T', ' ')}</span>}
          </div>
          <Button size="sm" disabled={busy || isRunning} onClick={start} data-testid="pull-active-serials-btn">
            {isRunning ? <><CircleNotch className="animate-spin" size={14}/> Running…</> : <><CloudArrowDown size={14} className="mr-1"/> Pull from Zoho</>}
          </Button>
        </div>

        {job.status === 'completed' && job.result && (
          <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs text-emerald-800" data-testid="pull-active-serials-result">
            Last run: <strong>{job.result.serials_upserted?.toLocaleString()}</strong> serials upserted
            from <strong>{job.result.items_with_serials?.toLocaleString()}</strong> items
            ({job.result.items_processed?.toLocaleString()} processed
            {job.result.remaining > 0 && <>, <strong>{job.result.remaining}</strong> items remaining — run again</>}).
          </div>
        )}
        {job.status === 'failed' && (
          <div className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-700">
            <XCircle size={12} className="inline mr-1"/> Failed: {job.error}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Per-record reconciliation table — read-only display of the
// invoice-by-invoice / bill-by-bill audit results.
// ──────────────────────────────────────────────────────────────────────
function PerRecordReconCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    zohoHistoricalAPI.reconPerRecord().then(r => setData(r.data)).catch(() => {});
  }, []);
  const reports = data?.reports || [];
  return (
    <Card data-testid="per-record-recon-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <MagnifyingGlass size={18}/> Per-Record Reconciliation (Zoho Dump vs DB)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-sm text-[#64748B]">
          Walks every row of the latest Zoho dump and compares against the matching DB collection.
          Identifies missing records, total mismatches, and serial-set differences.
        </p>
        {reports.length === 0 ? (
          <div className="text-xs text-[#94A3B8] italic">No reconciliation report yet — run the dump ingestion + recon script.</div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-[#F8FAFC] text-[#0F4C81]">
                <tr>
                  <th className="text-left p-2">Entity</th>
                  <th className="text-right p-2">Zoho</th>
                  <th className="text-right p-2">DB</th>
                  <th className="text-right p-2">Drift</th>
                  <th className="text-right p-2">Total ≠</th>
                  <th className="text-right p-2">Serial ≠</th>
                  <th className="text-right p-2">Only Zoho</th>
                  <th className="text-right p-2">Only DB</th>
                </tr>
              </thead>
              <tbody>
                {reports.map(r => {
                  const drift = (r.db_count || 0) - (r.zoho_count || 0);
                  const allOk = drift === 0 && (r.total_mismatch_count || 0) === 0 && (r.serial_mismatch_count || 0) === 0;
                  return (
                    <tr key={r.entity} className="border-t border-[#E2E8F0]">
                      <td className="p-2 font-medium">{r.entity}</td>
                      <td className="p-2 text-right">{r.zoho_count?.toLocaleString()}</td>
                      <td className="p-2 text-right">{r.db_count?.toLocaleString()}</td>
                      <td className={`p-2 text-right font-semibold ${drift === 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{drift >= 0 ? `+${drift}` : drift}</td>
                      <td className="p-2 text-right">{r.total_mismatch_count || 0}</td>
                      <td className="p-2 text-right">{r.serial_mismatch_count || 0}</td>
                      <td className="p-2 text-right">{r.only_in_zoho_count || 0}</td>
                      <td className="p-2 text-right">{r.only_in_db_count || 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Reconciliation Agent — Zoho vs DB drift table
// ──────────────────────────────────────────────────────────────────────
function ReconciliationCard() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const res = await zohoHistoricalAPI.reconStatus();
      setS(res.data);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const running = s?.job?.status === 'running';
    if (running && !pollRef.current) pollRef.current = setInterval(refresh, 3000);
    if (!running && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [s?.job?.status, refresh]);

  const start = async () => {
    setBusy(true);
    try { await zohoHistoricalAPI.reconRun(); toast.success('Reconciliation started'); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const job = s?.job || {};
  const rows = job?.result?.rows || [];
  const isRunning = job.status === 'running';

  return (
    <Card data-testid="reconciliation-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-base">
          <CheckCircle size={18}/> Zoho ↔ Fintexa Reconciliation
        </CardTitle>
        <Badge variant={isRunning ? 'default' : 'secondary'}>{job.status || 'idle'}</Badge>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-[#64748B]">
          One-shot drift report. For every entity, pulls Zoho's
          <code> page_context.total</code> and compares with the matching DB collection
          count. Use this before closing Zoho to confirm everything is mirrored.
          <strong> Cost: ~10 Zoho calls.</strong>
        </p>

        <div className="flex justify-end">
          <Button size="sm" disabled={busy || isRunning} onClick={start} data-testid="recon-run-btn">
            {isRunning ? <><CircleNotch className="animate-spin" size={14}/> Running…</> : <><MagnifyingGlass size={14} className="mr-1"/> Run Reconciliation</>}
          </Button>
        </div>

        {rows.length > 0 && (
          <div className="border rounded-lg overflow-hidden" data-testid="recon-result-table">
            <table className="w-full text-xs">
              <thead className="bg-[#F8FAFC] text-[#0F4C81]">
                <tr>
                  <th className="text-left p-2">Entity</th>
                  <th className="text-right p-2">Zoho</th>
                  <th className="text-right p-2">Fintexa DB</th>
                  <th className="text-right p-2">Drift</th>
                  <th className="text-left p-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const tone =
                    r.status === 'ok' ? 'text-emerald-700' :
                    r.status === 'missing' ? 'text-rose-700' :
                    r.status === 'extra' ? 'text-amber-700' :
                    r.status === 'info' ? 'text-[#64748B]' : 'text-[#64748B]';
                  return (
                    <tr key={i} className="border-t border-[#E2E8F0]">
                      <td className="p-2 font-medium">{r.entity}</td>
                      <td className="p-2 text-right">{r.zoho_count?.toLocaleString() ?? '—'}</td>
                      <td className="p-2 text-right">{r.db_count?.toLocaleString() ?? '—'}</td>
                      <td className={`p-2 text-right font-semibold ${tone}`}>{r.drift ?? '—'}</td>
                      <td className={`p-2 ${tone} uppercase text-[10px]`}>{r.status}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {job.completed_at && (
          <div className="text-[10px] text-[#64748B]">
            Last run: {job.completed_at.slice(0, 19).replace('T', ' ')} ·
            Zoho calls used: {job.result?.zoho_calls_used ?? '?'}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Deep Enrichment — CN line-items + Vendor Bill line-items + transfer stamp
// ──────────────────────────────────────────────────────────────────────
function DeepEnrichmentCard() {
  const [busy, setBusy] = useState({});
  const run = async (key, fn, label) => {
    setBusy(b => ({ ...b, [key]: true }));
    try { await fn(); toast.success(`${label} started`); }
    catch (e) { toast.error(e?.response?.data?.detail || `${label} failed`); }
    finally { setBusy(b => ({ ...b, [key]: false })); }
  };

  return (
    <Card data-testid="deep-enrichment-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Package size={18}/> Deep Enrichment (Line-Item Detail)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-[#64748B]">
          Zoho's list APIs return summaries without <code>line_items</code>. These jobs fetch
          per-record details for credit notes &amp; vendor bills (1 API call each), so the UI
          can show line-item breakdowns properly.
        </p>

        <div className="border rounded-lg p-3 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold">Credit Notes detail</div>
            <div className="text-[11px] text-[#64748B]">Pulls line items + linked invoice for every CN. ~485 calls.</div>
          </div>
          <Button size="sm" disabled={busy.cn} data-testid="enrich-cn-btn"
                  onClick={() => run('cn', zohoHistoricalAPI.enrichCreditNotes, 'CN enrichment')}>
            {busy.cn ? <><CircleNotch className="animate-spin" size={14}/></> : <>Run</>}
          </Button>
        </div>

        <div className="border rounded-lg p-3 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold">Vendor Bills detail (batch)</div>
            <div className="text-[11px] text-[#64748B]">Pulls line items + descriptions. 2,000 bills per run. ~2,000 calls. Repeat until 0 remaining.</div>
          </div>
          <Button size="sm" disabled={busy.vb} data-testid="enrich-vb-btn"
                  onClick={() => run('vb', () => zohoHistoricalAPI.enrichVendorBills(2000), 'VB enrichment')}>
            {busy.vb ? <><CircleNotch className="animate-spin" size={14}/></> : <>Run 2,000</>}
          </Button>
        </div>

        <div className="border rounded-lg p-3 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold">Stamp Transfer Serials onto Recon Bills/Invoices</div>
            <div className="text-[11px] text-[#64748B]">For inter-state transfer reconciliation docs only. DB-only, no Zoho calls.</div>
          </div>
          <Button size="sm" disabled={busy.tr} data-testid="stamp-tr-btn"
                  onClick={() => run('tr', zohoHistoricalAPI.stampTransferSerials, 'Transfer-serial stamp')}>
            {busy.tr ? <><CircleNotch className="animate-spin" size={14}/></> : <>Run</>}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Pass-2 Enrichment — fetches per-record details that the LIST API omits
// ──────────────────────────────────────────────────────────────────────
function EnrichmentCard() {
  const [enrichStatus, setEnrichStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const res = await zohoHistoricalAPI.enrichStatus();
      setEnrichStatus(res.data);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll when a job is running
  useEffect(() => {
    const running = enrichStatus?.job?.status === 'running';
    if (running && !pollRef.current) pollRef.current = setInterval(refresh, 4000);
    if (!running && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [enrichStatus?.job?.status, refresh]);

  const trigger = async (fn, label) => {
    setBusy(true);
    try { await fn(); toast.success(`${label} started`); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || `${label} failed`); }
    finally { setBusy(false); }
  };

  const job = enrichStatus?.job || {};
  const isRunning = job.status === 'running';
  const pendingInvoices = enrichStatus?.pending_invoices || 0;
  const enrichedInvoices = enrichStatus?.enriched_invoices || 0;
  const pendingSOs = enrichStatus?.pending_sales_orders || 0;
  const enrichedSOs = enrichStatus?.enriched_sales_orders || 0;
  const pendingBills = enrichStatus?.pending_bills || 0;
  const serialsCount = enrichStatus?.inventory_serials_count || 0;

  return (
    <Card data-testid="enrichment-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-base">
          <MagnifyingGlass size={18}/> Pass-2 Enrichment (Fill in missing detail data)
        </CardTitle>
        <Badge variant={isRunning ? 'default' : 'secondary'}>{job.status || 'idle'}</Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-[#64748B]">
          Zoho's list API returns only summary fields. Use these jobs to pull per-record details:
          item SKUs/GST/HSN, invoice line items, vendor-bill contents.
        </p>

        {/* Item resync — fast */}
        <div className="border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="enrich-items">
          <div className="flex items-start gap-2 flex-1">
            <Package size={20} className="text-emerald-700 mt-0.5"/>
            <div>
              <div className="text-sm font-semibold">Re-sync Items Master</div>
              <div className="text-xs text-[#64748B]">
                Fast (~6 API calls). Pulls SKU (`cf_product_code`), HSN, GST%, rate, stock. Already <strong>1,058 items</strong> in DB.
              </div>
            </div>
          </div>
          <Button size="sm" disabled={busy || isRunning} onClick={() => trigger(zohoHistoricalAPI.enrichItems, 'Items re-sync')} data-testid="enrich-items-btn">
            Re-sync Items
          </Button>
        </div>

        {/* Invoice line items */}
        <div className="border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="enrich-invoices">
          <div className="flex items-start gap-2 flex-1">
            <Receipt size={20} className="text-blue-700 mt-0.5"/>
            <div>
              <div className="text-sm font-semibold">Enrich Invoice Line Items + GST</div>
              <div className="text-xs text-[#64748B]">
                Slow (1 API call per invoice). Adds line items, item names, GST split. Daily Zoho cap is 10,000 calls — runs in batches of 500.
                {' '}<strong>{enrichedInvoices.toLocaleString()}</strong> done · <strong className="text-amber-700">{pendingInvoices.toLocaleString()}</strong> pending.
              </div>
            </div>
          </div>
          <Button size="sm" disabled={busy || isRunning || pendingInvoices === 0}
            onClick={() => trigger(() => zohoHistoricalAPI.enrichInvoices(500, 9000), 'Invoice enrichment (500)')}
            data-testid="enrich-invoices-btn">
            Run next 500
          </Button>
        </div>

        {/* SO line items */}
        <div className="border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="enrich-sos">
          <div className="flex items-start gap-2 flex-1">
            <Package size={20} className="text-indigo-700 mt-0.5"/>
            <div>
              <div className="text-sm font-semibold">Enrich Sales Order Line Items</div>
              <div className="text-xs text-[#64748B]">
                Adds items per SO (item names visible in Sales Orders list). 1 API call per SO.
                {' '}<strong>{enrichedSOs.toLocaleString()}</strong> done · <strong className="text-amber-700">{pendingSOs.toLocaleString()}</strong> pending.
              </div>
            </div>
          </div>
          <Button size="sm" disabled={busy || isRunning || pendingSOs === 0}
            onClick={() => trigger(() => zohoHistoricalAPI.enrichSalesOrders(500, 9000), 'SO enrichment (500)')}
            data-testid="enrich-sos-btn">
            Run next 500
          </Button>
        </div>

        {/* Bill line items + serials */}
        <div className="border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="enrich-bills">
          <div className="flex items-start gap-2 flex-1">
            <Barcode size={20} className="text-purple-700 mt-0.5"/>
            <div>
              <div className="text-sm font-semibold">Enrich Vendor Bills (line items)</div>
              <div className="text-xs text-[#64748B]">
                Adds purchased items per bill. <strong>{pendingBills.toLocaleString()}</strong> bills pending.
              </div>
            </div>
          </div>
          <Button size="sm" variant="outline" disabled={busy || isRunning || pendingBills === 0}
            onClick={() => trigger(zohoHistoricalAPI.enrichBillSerials, 'Bill enrichment')}
            data-testid="enrich-bills-btn">
            Run next 500
          </Button>
        </div>

        {/* Extract serial numbers from descriptions */}
        <div className="border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="enrich-serials-desc">
          <div className="flex items-start gap-2 flex-1">
            <Barcode size={20} className="text-amber-700 mt-0.5"/>
            <div>
              <div className="text-sm font-semibold">Extract Inventory Serials from Descriptions</div>
              <div className="text-xs text-[#64748B]">
                HearClear's Zoho stores serial numbers inside the line-item description text (e.g. <code>"Serial No : YT1261801014744"</code>).
                This pass scans every enriched invoice &amp; bill, extracts the serials via regex, and stores them in <code>inventory_serials</code>.
                <strong className="ml-1">No Zoho API calls.</strong>{' '}
                <strong>{serialsCount.toLocaleString()}</strong> serials already extracted.
                Run this <em>after</em> the invoice line-item enrichment.
              </div>
            </div>
          </div>
          <Button size="sm" variant="outline" disabled={busy || isRunning}
            onClick={() => trigger(zohoHistoricalAPI.enrichSerialsFromDescriptions, 'Serial extraction')}
            data-testid="enrich-serials-desc-btn">
            Extract Serials
          </Button>
        </div>

        {/* Clinic remap */}
        <div className="border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="remap-clinics">
          <div className="flex items-start gap-2 flex-1">
            <Barcode size={20} className="text-violet-700 mt-0.5"/>
            <div>
              <div className="text-sm font-semibold">Remap Invoices/SOs to Clinic Locations</div>
              <div className="text-xs text-[#64748B]">
                Re-resolves <code>clinic_id</code> / <code>location_id</code> / <code>branch_id</code> on
                every Zoho-synced invoice, SO, payment &amp; credit note using the latest clinic alias map.
                Fixes records where Zoho names like "HearClear Greater Kailash 2" weren't matched to
                "HearClear GK2" — these were invisible to clinic logins.
                <strong className="ml-1">No Zoho API calls.</strong>
              </div>
            </div>
          </div>
          <Button size="sm" variant="outline" disabled={busy || isRunning}
            onClick={() => trigger(zohoHistoricalAPI.remapClinics, 'Clinic remap')}
            data-testid="remap-clinics-btn">
            Remap Clinics
          </Button>
        </div>

        {/* SO Lead Source backfill */}
        <div className="border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="backfill-so-lead">
          <div className="flex items-start gap-2 flex-1">
            <Barcode size={20} className="text-indigo-700 mt-0.5"/>
            <div>
              <div className="text-sm font-semibold">Backfill Sales Order Lead Source &amp; Visit Type</div>
              <div className="text-xs text-[#64748B]">
                Re-pulls SO list pages from Zoho and updates <code>lead_source</code> + <code>visit_type</code> on existing records.
                Light: ~60 Zoho API calls for the full 6055-SO history. Run if SOs are missing Lead Source.
              </div>
            </div>
          </div>
          <Button size="sm" variant="outline" disabled={busy || isRunning}
            onClick={() => trigger(zohoHistoricalAPI.backfillSoLeadSource, 'SO lead source backfill')}
            data-testid="backfill-so-lead-btn">
            Backfill Lead Source
          </Button>
        </div>

        {/* Vendor Bill Detail Enrichment */}
        <div className="border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="enrich-vb-detail">
          <div className="flex items-start gap-2 flex-1">
            <Barcode size={20} className="text-orange-700 mt-0.5"/>
            <div>
              <div className="text-sm font-semibold">Enrich Vendor Bills (Line Items + Serials)</div>
              <div className="text-xs text-[#64748B]">
                Fetches DETAIL for vendor bills missing line items (~19k bills), parses description for
                serial numbers (e.g. <code>"Serial No : YT1261801014744"</code>), and writes them into
                <code> inventory_serials</code> marked <em>available</em> with vendor_bill_id + purchase date.
                Rate-limited: processes <strong>500 bills per run</strong>; run multiple times for full coverage.
              </div>
            </div>
          </div>
          <Button size="sm" variant="outline" disabled={busy || isRunning}
            onClick={() => trigger(() => zohoHistoricalAPI.enrichVendorBillsDetail(500), 'Vendor bill detail enrichment')}
            data-testid="enrich-vb-detail-btn">
            Enrich 500 Bills
          </Button>
        </div>

        {/* Cross-ref serials to line items */}
        <div className="border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="crossref-serials">
          <div className="flex items-start gap-2 flex-1">
            <Barcode size={20} className="text-emerald-700 mt-0.5"/>
            <div>
              <div className="text-sm font-semibold">Cross-Reference Serials → Invoices/SOs/Bills</div>
              <div className="text-xs text-[#64748B]">
                Stamps <code>serial_numbers</code> onto every invoice, SO and vendor-bill line item
                that has a matching record in <code>inventory_serials</code>. Run after the
                "Extract Serials from Descriptions" pass and the items-warehouse enrichment so they
                show up in the detail dialog &amp; PDF.
                <strong className="ml-1">No Zoho API calls.</strong>
              </div>
            </div>
          </div>
          <Button size="sm" variant="outline" disabled={busy || isRunning}
            onClick={() => trigger(zohoHistoricalAPI.crossrefSerials, 'Serial cross-reference')}
            data-testid="crossref-serials-btn">
            Cross-Ref Serials
          </Button>
        </div>

        {/* Invoice serial backfill from "Product Code" CF */}
        <div className="border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="backfill-inv-serials-pc">
          <div className="flex items-start gap-2 flex-1">
            <Barcode size={20} className="text-emerald-700 mt-0.5"/>
            <div>
              <div className="text-sm font-semibold">Backfill Invoice Serials (Real, from "Product Code")</div>
              <div className="text-xs text-[#64748B]">
                Re-fetches invoice detail and extracts <strong>real</strong> serial numbers from the
                line-item custom field <code>"Product Code"</code> (HearClear's Zoho convention for
                hearing-aid / charger inventory). Writes serials onto invoice line items AND upserts
                them into <code>inventory_serials</code> with <code>status="sold"</code> + buyer info.
                <strong className="ml-1">Heavy: ~32k Zoho API calls.</strong> Rate-limited 9k/day;
                resume-safe (idempotent flag on each invoice).
              </div>
            </div>
          </div>
          <Button size="sm" variant="outline" disabled={busy || isRunning}
            onClick={() => trigger(zohoHistoricalAPI.backfillInvoiceSerialsPC, 'Invoice serials (Product Code) backfill')}
            data-testid="backfill-inv-serials-pc-btn">
            Pull Real Invoice Serials
          </Button>
        </div>

        {/* Transfer Orders Sync */}
        <div className="border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="sync-transfer-orders">
          <div className="flex items-start gap-2 flex-1">
            <Barcode size={20} className="text-fuchsia-700 mt-0.5"/>
            <div>
              <div className="text-sm font-semibold">Sync Transfer Orders (with Serials)</div>
              <div className="text-xs text-[#64748B]">
                Pulls all 3,800+ Zoho transfer orders with line items, serial numbers, from→to
                clinics, and auto-created reconciliation bills/invoices (inter-state). Relocates each
                serial in <code>inventory_serials</code> to its current warehouse, builds
                <code> transfer_history[]</code>, and tags the auto-created bills/invoices with
                <code> is_transfer_reconciliation=true</code> so revenue/expense reports can exclude them.
                <strong className="ml-1">Heavy: ~3,800 Zoho API calls.</strong>
              </div>
            </div>
          </div>
          <Button size="sm" variant="outline" disabled={busy || isRunning}
            onClick={() => trigger(zohoHistoricalAPI.syncTransferOrders, 'Transfer orders sync')}
            data-testid="sync-transfer-orders-btn">
            Sync Transfer Orders
          </Button>
        </div>

        {/* FIFO admin tiles removed — only REAL Zoho serial data (from transfer-order sync
            and item-location enrichment) should populate inventory_serials. No heuristics. */}

        {/* Invoice Audiologist backfill */}
        <div className="border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="backfill-inv-audiologist">
          <div className="flex items-start gap-2 flex-1">
            <Barcode size={20} className="text-cyan-700 mt-0.5"/>
            <div>
              <div className="text-sm font-semibold">Backfill Invoice Audiologist Name</div>
              <div className="text-xs text-[#64748B]">
                Re-pulls invoice list pages and sets <code>audiologist.name</code> from Zoho's <code>salesperson_name</code>
                (the actual audiologist for HearClear). Replaces stale values like "Shubham Kotnala" appearing on every invoice.
                Light: ~165 Zoho API calls for 32k invoices.
              </div>
            </div>
          </div>
          <Button size="sm" variant="outline" disabled={busy || isRunning}
            onClick={() => trigger(zohoHistoricalAPI.backfillInvoiceAudiologist, 'Invoice audiologist backfill')}
            data-testid="backfill-inv-audiologist-btn">
            Backfill Audiologist
          </Button>
        </div>

        {/* Discount key DB backfill */}
        <div className="border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="backfill-disc-key">
          <div className="flex items-start gap-2 flex-1">
            <Barcode size={20} className="text-rose-700 mt-0.5"/>
            <div>
              <div className="text-sm font-semibold">Fix Line-Item Discount &amp; Amount Keys</div>
              <div className="text-xs text-[#64748B]">
                One-shot DB update: copies <code>discount_amount</code> → <code>discount</code> and
                <code> item_total</code>/<code>line_amount</code> → <code>amount</code> in every line item.
                <strong className="ml-1">No Zoho API calls.</strong> Fixes records mapped before the dual-key fix.
              </div>
            </div>
          </div>
          <Button size="sm" variant="outline" disabled={busy || isRunning}
            onClick={() => trigger(zohoHistoricalAPI.backfillDiscountKey, 'Line-item discount key backfill')}
            data-testid="backfill-disc-key-btn">
            Run Backfill
          </Button>
        </div>

        {/* GST split (CGST/SGST vs IGST) backfill */}
        <div className="border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="backfill-gst-split">
          <div className="flex items-start gap-2 flex-1">
            <Barcode size={20} className="text-indigo-700 mt-0.5"/>
            <div>
              <div className="text-sm font-semibold">Fix CGST/SGST vs IGST (inter-state)</div>
              <div className="text-xs text-[#64748B]">
                One-shot DB update: recomputes each invoice's &amp; credit note's tax split from the
                seller (clinic) state vs the customer's place-of-supply. Zoho-imported inter-state sales
                (e.g. Delhi&nbsp;→&nbsp;Maharashtra) were stored as CGST/SGST with IGST&nbsp;₹0 — this switches
                them to IGST so the invoice detail, PDF and GST summary are correct. Totals are unchanged.
                <strong className="ml-1">No Zoho API calls.</strong> Takes ~1 min for ~35k invoices.
              </div>
            </div>
          </div>
          <Button size="sm" variant="outline" disabled={busy || isRunning}
            onClick={async () => {
              setBusy(true);
              try {
                const { data } = await zohoHistoricalAPI.backfillGstSplit();
                const inv = data?.billing_invoices?.updated ?? 0;
                const cn = data?.billing_credit_notes?.updated ?? 0;
                toast.success(`GST split fixed: ${inv} invoices, ${cn} credit notes updated`);
                refresh();
              } catch (e) { toast.error(e?.response?.data?.detail || 'GST split backfill failed'); }
              finally { setBusy(false); }
            }}
            data-testid="backfill-gst-split-btn">
            Fix Tax Split
          </Button>
        </div>


        {/* Customer dedupe */}
        <div className="border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="dedupe-customers">
          <div className="flex items-start gap-2 flex-1">
            <Barcode size={20} className="text-teal-700 mt-0.5"/>
            <div>
              <div className="text-sm font-semibold">Deduplicate Customers</div>
              <div className="text-xs text-[#64748B]">
                Finds customer records that share the same GSTIN <em>and</em> normalized display name,
                keeps the oldest as canonical, and re-points all invoices, SOs, payments &amp; credit notes
                to it. Removes the dupes. Safe — only merges exact name+GSTIN matches.
                <strong className="ml-1">No Zoho API calls.</strong>
              </div>
            </div>
          </div>
          <Button size="sm" variant="outline" disabled={busy || isRunning}
            onClick={() => trigger(zohoHistoricalAPI.dedupeCustomers, 'Customer dedupe')}
            data-testid="dedupe-customers-btn">
            Dedupe Customers
          </Button>
        </div>

        {/* Job status */}
        {isRunning && job.progress && (
          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm" data-testid="enrich-progress">
            <div className="flex items-center gap-2 mb-1">
              <CircleNotch size={14} className="animate-spin text-blue-700"/>
              <strong className="text-blue-700">{job.type} running</strong>
            </div>
            <div className="text-xs text-blue-700">
              Processed: <strong>{job.progress.processed || 0}</strong>
              {job.progress.errors !== undefined && <> · Errors: <strong>{job.progress.errors || 0}</strong></>}
              {job.progress.serials_added !== undefined && <> · Serials added: <strong>{job.progress.serials_added || 0}</strong></>}
            </div>
          </div>
        )}
        {job.status === 'completed' && job.result && (
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm text-emerald-700" data-testid="enrich-result">
            <div className="flex items-center gap-2"><CheckCircle size={14}/> Last run: <strong>{job.type}</strong></div>
            <pre className="text-xs mt-1">{JSON.stringify(job.result, null, 2)}</pre>
          </div>
        )}
        {job.status === 'failed' && (
          <div className="bg-rose-50 border border-rose-200 rounded p-3 text-sm text-rose-700">
            <XCircle size={14} className="inline mr-1"/> Last run failed: {job.error}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
